import React, { ReactNode } from "react";
import styled from "styled-components";
import {
  ImageCopyItem,
  ImageCopyItemProps,
  PageSection,
  PageSectionProps,
  Theme,
  typography,
} from "@literati/public-ui-shared-react";

/** This is the className that will be pre-pended to all of TeacherPartnerSectionAboutClubs's inner component classNames */
export const TEACHER_PARTNER_SECTION_ABOUT_CLUBS_CLASS_BASE =
  "teacher-partner-section-about-clubs";

/** These are all of the internal classNames that are used within the TeacherPartnerSectionAboutClubs component */
export const TeacherPartnerSectionAboutClubsClassName = {
  BASE: TEACHER_PARTNER_SECTION_ABOUT_CLUBS_CLASS_BASE,
  CONTENT: `${TEACHER_PARTNER_SECTION_ABOUT_CLUBS_CLASS_BASE}-content`,
  EYEBROW: `${TEACHER_PARTNER_SECTION_ABOUT_CLUBS_CLASS_BASE}-eyebrow`,
  HEADING: `${TEACHER_PARTNER_SECTION_ABOUT_CLUBS_CLASS_BASE}-heading`,
  ITEMS_GRID: `${TEACHER_PARTNER_SECTION_ABOUT_CLUBS_CLASS_BASE}-items-grid`,
} as const;

export interface TeacherPartnerSectionAboutClubsProps
  extends Omit<PageSectionProps, "children"> {
  className?: string;
  insetCardEyebrow: ReactNode;
  items?: ImageCopyItemProps[];
  insetCardHeading: ReactNode;
  insetCardSubHeading?: ReactNode;
}

const AboutClubsContent = styled.div`
  margin: 3rem 0;
  padding: 0 1rem;

  ${({ theme }) => theme.media.sm} {
    margin: 3rem 1rem;
  }

  ${({ theme }) => theme.media.lg} {
    margin: 3rem;
  }
`;

const StyledH2 = styled.h2`
  ${typography.eyebrow}
`;

const StyledP = styled.p`
  margin: 1rem 0 0;
  font-family: ${({ theme }) => theme.font.fontFamily.hoeflerTitling};
  font-size: 2rem;
`;

const AboutClubsItemsGrid = styled.div`
  display: grid;
  grid-template-rows: repeat(3, 1fr);
  grid-template-columns: 1fr;
  gap: 2.625rem 0;
  justify-items: center;
  margin: 2rem 0;
  padding: 0.5rem;

  ${({ theme }) => theme.media.lg} {
    margin: 3rem 0;
    gap: 0 2rem;
    padding: 0;
  }

  ${({ theme }) => theme.media.lg} {
    grid-template-rows: 1fr;
    grid-template-columns: repeat(3, 1fr);
    gap: 0 3rem;
  }
`;

const AboutClubsImageCopyItem = styled(ImageCopyItem)`
  .image-copy-item-primary-copy {
    margin: 1rem 0 0;
  }
`;

export const ITEMS_DEFAULT = [
  {
    imageProps: {
      alt: "Literati book box",
      height: 460,
      src: "https://images.ctfassets.net/vqtz4cq2w5ts/1qcMA428ZpEluImvCVRU1O/f001ce4482810cf95274c8ee441291da/Teacher_Partner_LP_PersonalizedPicks_2x.png",
      targetHeight: 230,
      width: 460,
    },
    primaryCopy: "Try Five Personalized Picks",
    subtext:
      "Our experts send books that match each reader’s level and interests.",
  },
  {
    imageProps: {
      alt: "Kids reading books in a tent with a flashlight.",
      height: 460,
      src: "https://images.ctfassets.net/vqtz4cq2w5ts/3zEeEGyBaD9qR5OI7Zcxnq/4e8f6230e8e12fe7658f47f6c532297e/Teacher_Partner_LP_BetterWithEveryBox_2x.png",
      targetHeight: 230,
      width: 460,
    },
    primaryCopy: "Better with Every Box",
    subtext: "Curation improves and adapts to fit readers as they progress.",
  },
  {
    imageProps: {
      alt: "Person putting return sticker on Literati box.",
      height: 460,
      src: "https://images.ctfassets.net/vqtz4cq2w5ts/6FnrVsGJO8y9txFSGXtt9U/8d191aee423f8cabd728976d40b69dde/Teacher_Partner_LP_FreeUnlimitedReturns_2x.png",
      targetHeight: 230,
      width: 460,
    },
    primaryCopy: "Only Keep Their Favorites",
    subtext:
      "They keep the books they love, and send back anything they don't.",
  },
];

export const TeacherPartnerSectionAboutClubs = ({
  className = "",
  insetCardEyebrow,
  items = ITEMS_DEFAULT,
  insetCardHeading,
  insetCardSubHeading,
  ...pageSectionProps
}: TeacherPartnerSectionAboutClubsProps) => {
  const cls = TeacherPartnerSectionAboutClubsClassName;

  return (
    <Theme>
      <PageSection
        bgColor="periwinkleTint5"
        className={`${cls.BASE} ${className}`}
        insetCard
        {...pageSectionProps}
      >
        <AboutClubsContent className={cls.CONTENT}>
          <StyledH2 className={cls.EYEBROW}>{insetCardEyebrow}</StyledH2>
          <StyledP className={cls.HEADING}>{insetCardHeading}</StyledP>
          {insetCardSubHeading ? (
            <StyledP className={cls.HEADING}>{insetCardSubHeading}</StyledP>
          ) : null}
          <AboutClubsItemsGrid className={cls.ITEMS_GRID}>
            {items?.map((content, idx) => (
              <AboutClubsImageCopyItem
                key={`about-clubs-item-${idx}`}
                variant="small"
                {...content}
              />
            ))}
          </AboutClubsItemsGrid>
        </AboutClubsContent>
      </PageSection>
    </Theme>
  );
};

export default TeacherPartnerSectionAboutClubs;
